/**
 * Import Monitor Health Check tab.
 *
 * CR-5318: this tab is the "Exceptions" view — it surfaces every recent import
 * job that failed or partially failed so an admin can see at a glance which
 * rows didn't make it in, click through to the per-row error log, and triage.
 */
import {
  CheckCircle2, AlertCircle, ChevronRight,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '../../components/ui/badge';
import { type ImportJob } from '../../lib/api/imports';

interface ImportHealthCheckTabProps {
  /** Supplier id to scope checks against. */
  supplierId: number;
  /** Recent failed/partial jobs from the dashboard summary. */
  recentFailures: ImportJob[];
  /** Total job-level failures today. */
  jobsFailedToday: number;
  /** Sum of failed rows across today's jobs. */
  recordsFailedToday: number;
  /** Open the per-job drill-down modal so admins can see the per-row errors. */
  onSelectJob: (jobId: string) => void;
}

export default function ImportHealthCheckTab({
  recentFailures,
  jobsFailedToday,
  recordsFailedToday,
  onSelectJob,
}: ImportHealthCheckTabProps) {
  const totalFailedRecords = recentFailures.reduce((sum, j) => sum + (j.records_failed || 0), 0);

  return (
    <div className="space-y-6">
      {/* Exceptions summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border rounded-lg p-3 bg-red-50/50 border-red-200">
          <div className="text-xs text-red-700 uppercase font-semibold">Failed Today</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{jobsFailedToday}</div>
          <div className="text-xs text-red-700/80 mt-0.5">
            {recordsFailedToday.toLocaleString()} record{recordsFailedToday === 1 ? '' : 's'}
          </div>
        </div>
        <div className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
          <div className="text-xs text-amber-700 uppercase font-semibold">Recent Exception Jobs</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{recentFailures.length}</div>
          <div className="text-xs text-amber-700/80 mt-0.5">
            {totalFailedRecords.toLocaleString()} failed record{totalFailedRecords === 1 ? '' : 's'} total
          </div>
        </div>
      </div>

      {/* Recent exceptions list */}
      <div className="border rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <div>
            <h3 className="font-medium text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              Import Exceptions
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Jobs that failed or partially failed. Click any row to inspect the per-row error log.
            </p>
          </div>
        </div>

        {recentFailures.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-10">
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
            No import exceptions recorded for this source.
          </div>
        ) : (
          <div className="divide-y">
            {recentFailures.map((job) => {
              const completedDate = job.completed_at || job.started_at || job.created_at;
              return (
                <button
                  key={job.id}
                  type="button"
                  onClick={() => onSelectJob(job.job_id)}
                  className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-muted/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{job.data_type_display}</span>
                      <Badge
                        variant="outline"
                        className={
                          job.status === 'failed'
                            ? 'text-red-700 border-red-300 bg-red-50 text-[10px]'
                            : 'text-amber-700 border-amber-300 bg-amber-50 text-[10px]'
                        }
                      >
                        {job.status_display}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{job.supplier_name}</span>
                    </div>
                    {job.error_message && (
                      <p className="text-xs text-red-700 line-clamp-2 font-mono">
                        {job.error_message}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>
                        <span className="font-semibold text-red-700">{job.records_failed.toLocaleString()}</span>
                        <span> failed of </span>
                        <span>{job.total_records.toLocaleString()}</span>
                      </span>
                      {completedDate && (
                        <span>
                          {formatDistanceToNow(new Date(completedDate), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
